import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import { Paid } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../context/studentAuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ActivePayments = ({ formatDate, formatCurrency }) => {
  const { student } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: '' });
  const processedInstallmentVerifyRef = useRef(new Set());

  useEffect(() => {
    if (!student?.id) return;

    const fetchPayments = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/students/active-payment/${student.id}`);
        setCourses(res.data);
      } catch (error) {
        console.error('Error fetching active payments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [student?.id]);

  useEffect(() => {
    const verifyInstallmentFromRedirect = async () => {
      const params = new URLSearchParams(window.location.search);
      const paymentType = params.get('payment');
      const reference = params.get('reference') || params.get('trxref');
      const enrollmentId = params.get('enrollmentId');
      const dueDate = params.get('dueDate');

      if (paymentType !== 'installment' || !reference || !student?.id) {
        return;
      }

      if (processedInstallmentVerifyRef.current.has(reference)) {
        return;
      }
      processedInstallmentVerifyRef.current.add(reference);

      try {
        await axios.post(`${BASE_URL}/api/students/verify-installment-payment`, {
          reference,
          enrollment_id: enrollmentId,
          due_date: dueDate,
          student_id: student.id,
          email: student.email,
          name: student.name,
        });

        setSnackbar({
          open: true,
          message: 'Installment payment verified successfully!',
          severity: 'success'
        });

        const res = await axios.get(`${BASE_URL}/api/students/active-payment/${student.id}`);
        setCourses(res.data);
      } catch (err) {
        console.error('Installment verification failed:', err.response?.data || err.message);
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || 'Failed to verify installment. Contact support.',
          severity: 'error'
        });
      } finally {
        window.history.replaceState({}, document.title, '/payment');
      }
    };

    verifyInstallmentFromRedirect();
  }, [student?.id]);

  const handleInstallmentPayment = async (course, installment) => {

    if (!installment.date) {
      console.error('Missing due date');
      setSnackbar({
        open: true,
        message: 'Installment due date missing. Contact support.',
        severity: 'error'
      });
      return;
    }

    const date = new Date(installment.date);
    const formattedDueDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    try {
      const initRes = await axios.post(`${BASE_URL}/api/students/initialize-installment-payment`, {
        enrollment_id: course.id,
        due_date: formattedDueDate,
        student_id: student.id,
        email: student.email,
        name: student.name,
        amount: installment.amount,
      });

      const authorizationUrl = initRes.data?.authorization_url;
      if (!authorizationUrl) {
        setSnackbar({
          open: true,
          message: initRes.data?.message || 'Unable to initialize installment payment.',
          severity: 'error'
        });
        return;
      }

      window.location.href = authorizationUrl;
    } catch (err) {
      console.error('Installment initialize failed:', err.response?.data || err.message);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Unable to initialize installment payment.',
        severity: 'error'
      });
    }
  };


  if (loading) return <Typography>Loading...</Typography>;
  if (!courses.length) return <Typography>No active payments found.</Typography>;

  return (
    <Grid container spacing={3}>
      {courses.map((course) => (
        <Grid size={{ xs: 12 }} key={course.id}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{course.title}</Typography>
                <Chip
                  label={`${Math.round((course.amountPaid / course.totalAmount) * 100)}% Paid`}
                  color="primary"
                />
              </Box>

              <Typography color="text.secondary" gutterBottom>
                Instructor: {course.instructor}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={(course.amountPaid / course.totalAmount) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee' }}>
                    <Typography variant="subtitle2">Total Amount</Typography>
                    <Typography variant="h6">{formatCurrency(course.totalAmount)}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee' }}>
                    <Typography variant="subtitle2">Amount Paid</Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(course.amountPaid)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee' }}>
                    <Typography variant="subtitle2">Next Payment Due</Typography>
                    <Typography variant="h6">
                      {formatDate(course.nextPaymentDate)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Installment Plan
              </Typography>

              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {course.installments.map((installment, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDate(installment.date)}</TableCell>
                        <TableCell>{formatCurrency(installment.amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={installment.status}
                            color={installment.status === 'paid' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {installment.status === 'pending' && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Paid />}
                              onClick={() => handleInstallmentPayment(course, installment)}
                            >
                              Make Payment
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      ))}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ActivePayments;
